import React, { useCallback, useContext, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../reducers'
import { useForm } from 'react-hook-form'
import { SkillBiteDetailsForm } from '../../../components'
import { Box, CircularProgress, Fab } from '@mui/material'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { skillBiteFormSchema } from '../../../util/skill-bite-schemas'
import { yupResolver } from '@hookform/resolvers/yup'
import { TabChangeGuardContext } from '..'
import { useConfirm } from 'material-ui-confirm'
import { useBeforeUnload } from '../../../hooks'
import { unstable_usePrompt as usePrompt } from 'react-router-dom'

const useStyles = makeStyles(() => ({
  root: {
    padding: '16px'
  }
}))

const Details = () => {
  const classes = useStyles()
  const { tabChangeGuard } = useContext(TabChangeGuardContext)
  const confirm = useConfirm()
  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const isPublished = useSelector(SkillBiteEditSlice.selectors.selectIsPublished)
  const { name, shortDescription, description, image, metaSkill, skill, expert, credlyBadgeTemplateId } = skillBite
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)

  const form = useForm({
    defaultValues: {
      name,
      description,
      shortDescription,
      expert,
      image: {
        ...image
      },
      metaSkill: metaSkill || '',
      skill: skill || '',
      credlyBadgeTemplateId: credlyBadgeTemplateId || ''
    },
    resolver: yupResolver(skillBiteFormSchema),
    mode: 'all'
  })

  const [updateSkillBiteDetails] = useEvent([SkillBiteEditSlice.eventBus.updateSkillBiteDetails({
    onSuccess: (payload) => {
      form.reset({
        name: payload.name,
        description: payload.description,
        shortDescription: payload.shortDescription,
        expert: payload.expert,
        image: {
          ...payload.image
        },
        metaSkill: payload.metaSkill || '',
        skill: payload.skill || '',
        credlyBadgeTemplateId: payload.credlyBadgeTemplateId || ''
      })
    }
  })])

  const { isValid, isDirty } = form.formState

  // warns the user if they try to leave without saving the changes they made
  useBeforeUnload(() => isDirty && canEdit)
  usePrompt({
    message: 'You have unsaved changes on your Skill Bite Details form, are you sure you want to exit without saving?',
    when: isDirty && canEdit
  })

  const discardChangesOnNavigationGuard = useCallback((callback) => {
    if (isDirty && canEdit) {
      confirm({
        title: 'Discard changes?',
        description: 'You have unsaved changes on your Skill Bite Details form, are you sure you want to exit without saving?',
        confirmationText: 'Yes, discard',
        cancellationText: 'No, keep editing'
      })
        .then(callback)
        .catch(() => { })
      return
    }

    callback()
  }, [confirm, isDirty, canEdit])

  useEffect(() => {
    if (tabChangeGuard) {
      tabChangeGuard.current = discardChangesOnNavigationGuard
    }
    return () => {
      if (tabChangeGuard) {
        tabChangeGuard.current = null
      }
    }
  }, [tabChangeGuard, discardChangesOnNavigationGuard])

  const onSubmit = useCallback((data) => {
    updateSkillBiteDetails.trigger({ ...data, skillBiteId: skillBite.id })
  }, [updateSkillBiteDetails, skillBite.id])

  return (
    <div className={classes.root}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <SkillBiteDetailsForm
          form={form}
          errors={form.errors}
          isReadOnly={updateSkillBiteDetails.isWorking || !canEdit}
        />
        {canEdit && (
          <Box display='flex' flexDirection='row' justifyContent='flex-end' sx={{ position: 'sticky', bottom: 16, right: 16, mt: 2, mb: 2 }}>
            <Fab variant='extended' color='primary' size='medium' type='submit' disabled={updateSkillBiteDetails.isWorking || !isValid || !isDirty}>
              {updateSkillBiteDetails.isWorking ? <CircularProgress size='1em' sx={{ mr: 1 }} /> : <SaveRoundedIcon sx={{ mr: 1 }} />}
              {isPublished ? 'Save & Publish' : 'Save'}
            </Fab>
          </Box>
        )}
      </form>
    </div>
  )
}

export default Details
